import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

/**
 * Widok krzyżowy 3D (fence diagram) — renderuje obie linie tak, żeby było widać
 * dokładnie warstwy „przerobione" w edytorze SVG (manualConnections + freeDraw).
 *
 * Źródła geometrii (per linia):
 *   1. manualConnections normalne — łączą warstwę X w otworze A z warstwą Y w otworze B
 *      (strop-do-stropu, spąg-do-spągu, kolor z connection).
 *   2. manualConnections freeDraw — polygon w 2D z punktami {distanceM, depthM},
 *      mapowany na ścianę 3D przez fan triangulation.
 *   3. Fallback gdy brak manualConnections: matching warstw po name między sąsiednimi
 *      otworami (back-compat z poprzednią wersją, ale po nazwie warstwy nie po indeksie).
 *
 * Mapowanie 2D→3D:
 *   x,y w PL-2000 = interpolacja liniowa coordX/coordY otworów wg distanceM
 *   z w m n.p.m.  = terrainProfile(distanceM) − depthM   (fallback: liniowa interp depth otworów)
 */
export default function CrossFence3DView({ dataByLine }) {
  const { traces, hasGeometry, warning } = useMemo(() => {
    const t = [];
    let fallbackUsed = false;
    // Akceptuj zarowno obiekt { 1: data, 2: data, ...} jak i tablicę
    const entries = dataByLine && typeof dataByLine === 'object'
      ? Object.entries(dataByLine).map(([k, v]) => [Number(k) || k, v])
      : [];
    for (const [lineId, data] of entries) {
      if (!data || !Array.isArray(data.boreholes) || data.boreholes.length < 2) continue;
      const r = buildFenceTraces(data, `Linia ${lineId}`);
      t.push(...r.traces);
      if (r.fallback) fallbackUsed = true;
    }
    return {
      traces: t,
      hasGeometry: t.length > 0,
      warning: fallbackUsed
        ? 'Brak manualConnections w przynajmniej jednej linii — używam fallback (matching po nazwie warstwy). Wejdź do edytora linii i połącz warstwy, żeby zobaczyć dokładny 3D.'
        : null,
    };
  }, [dataByLine]);

  if (!hasGeometry) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="text-lg mb-2">Brak danych do podglądu 3D</div>
        <div className="text-sm">Potrzeba min. 2 otwory ze współrzędnymi XY w przynajmniej jednej linii.</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg shadow-lg p-3">
        <div className="text-xs text-gray-500 mb-2 flex items-center gap-2 flex-wrap">
          <span>🔀 Fence diagram 3D — obracaj LPM, zoom scroll, pan PPM.</span>
          {warning && (
            <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
              ⚠ {warning}
            </span>
          )}
        </div>
        <Plot
          data={traces}
          layout={{
            scene: {
              xaxis: { title: 'X (PL-2000)' },
              yaxis: { title: 'Y (PL-2000)' },
              zaxis: { title: 'Z (m n.p.m.)' },
              aspectmode: 'data',
              camera: { eye: { x: 1.6, y: 1.6, z: 1.0 } },
            },
            margin: { l: 0, r: 0, t: 0, b: 0 },
            autosize: true,
            height: Math.max(600, window.innerHeight - 200),
            showlegend: true,
            legend: { x: 0, y: 1, bgcolor: 'rgba(255,255,255,0.85)', font: { size: 11 } },
          }}
          config={{
            responsive: true,
            displayModeBar: true,
            displaylogo: false,
            modeBarButtonsToRemove: ['toImage'],
          }}
          style={{ width: '100%' }}
          useResizeHandler
        />
      </div>
    </div>
  );
}

/**
 * Buduje mesh3d traces dla jednej linii.
 * @returns {{ traces: Array, fallback: boolean }}
 */
function buildFenceTraces(data, nameLabel) {
  const empty = { traces: [], fallback: false };
  if (!data) return empty;

  // Akceptuj zarowno FLAT format (data.manualConnections — z onDataChange) jak i NESTED
  // (data.connections.manualConnections — z serializeProject/Firestore). To samo dla settings.
  const boreholes = Array.isArray(data.boreholes) ? data.boreholes : [];
  const manualConnections = Array.isArray(data.manualConnections)
    ? data.manualConnections
    : (Array.isArray(data.connections?.manualConnections) ? data.connections.manualConnections : []);
  const terrainProfile = Array.isArray(data.terrainProfile) ? data.terrainProfile : null;

  const valid = boreholes.filter(b => b.coordX != null && b.coordY != null && Array.isArray(b.layers));
  if (valid.length < 2) return empty;

  // Skumulowane dystanse rzeczywiste (od pierwszego otworu, wzdłuż polilinii w XY)
  const cumDist = [0];
  for (let i = 1; i < valid.length; i++) {
    const dx = valid[i].coordX - valid[i - 1].coordX;
    const dy = valid[i].coordY - valid[i - 1].coordY;
    cumDist.push(cumDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }
  const idById = new Map(valid.map((b, i) => [String(b.id), i]));

  // Helper — pozycja XY na linii w danym distance
  const positionAt = (distM) => {
    if (distM <= 0) return { x: valid[0].coordX, y: valid[0].coordY };
    if (distM >= cumDist[cumDist.length - 1]) return { x: valid[valid.length - 1].coordX, y: valid[valid.length - 1].coordY };
    let seg = 0;
    for (let i = 0; i < cumDist.length - 1; i++) {
      if (distM >= cumDist[i] && distM <= cumDist[i + 1]) { seg = i; break; }
    }
    const denom = cumDist[seg + 1] - cumDist[seg] || 1;
    const t = (distM - cumDist[seg]) / denom;
    return {
      x: valid[seg].coordX + t * (valid[seg + 1].coordX - valid[seg].coordX),
      y: valid[seg].coordY + t * (valid[seg + 1].coordY - valid[seg].coordY),
    };
  };

  // Helper — rzędna terenu w danym distance (terrainProfile albo fallback z borehole.depth)
  const elevAt = (distM) => {
    if (terrainProfile && terrainProfile.length >= 2) {
      const last = terrainProfile[terrainProfile.length - 1];
      if (distM <= terrainProfile[0].distance) return terrainProfile[0].elevation;
      if (distM >= last.distance) return last.elevation;
      for (let i = 0; i < terrainProfile.length - 1; i++) {
        const p1 = terrainProfile[i], p2 = terrainProfile[i + 1];
        if (distM >= p1.distance && distM <= p2.distance) {
          const t = (distM - p1.distance) / (p2.distance - p1.distance || 1);
          return p1.elevation + t * (p2.elevation - p1.elevation);
        }
      }
    }
    // Fallback: interpolacja między borehole.depth (rzędna terenu na otworach)
    if (distM <= 0) return valid[0].depth || 0;
    if (distM >= cumDist[cumDist.length - 1]) return valid[valid.length - 1].depth || 0;
    let seg = 0;
    for (let i = 0; i < cumDist.length - 1; i++) {
      if (distM >= cumDist[i] && distM <= cumDist[i + 1]) { seg = i; break; }
    }
    const t = (distM - cumDist[seg]) / (cumDist[seg + 1] - cumDist[seg] || 1);
    return (valid[seg].depth || 0) + t * ((valid[seg + 1].depth || 0) - (valid[seg].depth || 0));
  };

  // Helper — strop/spąg warstwy o danym id w otworze (w m n.p.m.)
  const findLayerBounds = (bh, layerId) => {
    let top = bh.depth || 0;
    for (const layer of bh.layers) {
      const bot = top - (layer.thickness || 0);
      if (String(layer.id) === String(layerId)) return { top, bot, color: layer.color, name: layer.name };
      top = bot;
    }
    return null;
  };

  // Akumulator traces per kolor (mniej traces = lżejszy render Plotly)
  const traceByColor = new Map();
  const ensureTrace = (color) => {
    if (!traceByColor.has(color)) traceByColor.set(color, { x: [], y: [], z: [], i: [], j: [], k: [] });
    return traceByColor.get(color);
  };
  const addQuad = (color, p1, p2, p3, p4) => {
    const t = ensureTrace(color);
    const b = t.x.length;
    t.x.push(p1.x, p2.x, p3.x, p4.x);
    t.y.push(p1.y, p2.y, p3.y, p4.y);
    t.z.push(p1.z, p2.z, p3.z, p4.z);
    t.i.push(b + 0, b + 0);
    t.j.push(b + 1, b + 2);
    t.k.push(b + 2, b + 3);
  };
  const addPolygon = (color, points3D) => {
    if (points3D.length < 3) return;
    const t = ensureTrace(color);
    const base = t.x.length;
    for (const p of points3D) { t.x.push(p.x); t.y.push(p.y); t.z.push(p.z); }
    // Fan triangulation z wierzchołka 0
    for (let pi = 1; pi < points3D.length - 1; pi++) {
      t.i.push(base + 0);
      t.j.push(base + pi);
      t.k.push(base + pi + 1);
    }
  };

  // 1. manualConnections — normalne (warstwa↔warstwa) i freeDraw (polygon)
  let usedManual = 0;
  for (const conn of manualConnections) {
    if (conn.fromLayerId === 'freeDraw' || conn.toLayerId === 'freeDraw') {
      // freeDraw polygon — points w {distanceM, depthM}
      if (!Array.isArray(conn.points) || conn.points.length < 3) continue;
      const points3D = conn.points.map(p => {
        if (p.distanceM == null || p.depthM == null) return null;
        const xy = positionAt(p.distanceM);
        const elev = elevAt(p.distanceM);
        return { x: xy.x, y: xy.y, z: elev - p.depthM };
      }).filter(Boolean);
      if (points3D.length >= 3) {
        addPolygon(conn.color || '#888888', points3D);
        usedManual++;
      }
      continue;
    }
    // Normalne połączenie — łączy strop/spąg warstwy fromLayerId z toLayerId
    const fromIdx = idById.get(String(conn.fromBoreholeId));
    const toIdx = idById.get(String(conn.toBoreholeId));
    if (fromIdx == null || toIdx == null) continue;
    const fromBh = valid[fromIdx];
    const toBh = valid[toIdx];
    const f = findLayerBounds(fromBh, conn.fromLayerId);
    const t = findLayerBounds(toBh, conn.toLayerId);
    if (!f || !t) continue;
    const color = conn.color || f.color || '#888888';
    const pF = { x: fromBh.coordX, y: fromBh.coordY };
    const pT = { x: toBh.coordX, y: toBh.coordY };
    addQuad(color,
      { x: pF.x, y: pF.y, z: f.top },
      { x: pT.x, y: pT.y, z: t.top },
      { x: pT.x, y: pT.y, z: t.bot },
      { x: pF.x, y: pF.y, z: f.bot },
    );
    usedManual++;
  }

  // 2. Fallback gdy brak manualConnections — match warstw po `name` między sąsiednimi otworami
  let fallbackUsed = false;
  if (usedManual === 0) {
    fallbackUsed = true;
    for (let i = 0; i < valid.length - 1; i++) {
      const bh1 = valid[i];
      const bh2 = valid[i + 1];
      // Wylicz strop/spąg każdej warstwy w obu otworach
      const bounds1 = computeAllLayerBounds(bh1);
      const bounds2 = computeAllLayerBounds(bh2);
      // Match po nazwie — pierwsza warstwa o danej nazwie w obu otworach
      const usedNames2 = new Set();
      for (const b1 of bounds1) {
        const b2 = bounds2.find(b => b.name === b1.name && !usedNames2.has(b.name));
        if (!b2) continue;
        usedNames2.add(b2.name);
        const color = b1.color || b2.color || '#888888';
        addQuad(color,
          { x: bh1.coordX, y: bh1.coordY, z: b1.top },
          { x: bh2.coordX, y: bh2.coordY, z: b2.top },
          { x: bh2.coordX, y: bh2.coordY, z: b2.bot },
          { x: bh1.coordX, y: bh1.coordY, z: b1.bot },
        );
      }
    }
  }

  // Konwersja do traces
  const traces = [];
  let idx = 0;
  for (const [color, td] of traceByColor.entries()) {
    if (td.x.length === 0) continue;
    traces.push({
      type: 'mesh3d',
      x: td.x, y: td.y, z: td.z,
      i: td.i, j: td.j, k: td.k,
      color,
      opacity: 0.92,
      flatshading: true,
      name: idx === 0 ? nameLabel : `${nameLabel} #${idx + 1}`,
      showlegend: idx === 0,
      hoverinfo: 'name',
    });
    idx++;
  }
  return { traces, fallback: fallbackUsed };
}

function computeAllLayerBounds(bh) {
  const out = [];
  let top = bh.depth || 0;
  for (const layer of bh.layers || []) {
    const bot = top - (layer.thickness || 0);
    out.push({ id: layer.id, name: layer.name, color: layer.color, top, bot });
    top = bot;
  }
  return out;
}
